import { digitsEnToFa } from '@persian-tools/persian-tools';
import moment from 'jalali-moment';

export interface ReleaseAtLabel {
  /** «شنبه ۲ آبان» — weekday first: release days are remembered by weekday («شنبه‌ها ساعت ۸»). */
  day: string;
  /** «شنبه» alone, for tight spots. */
  weekday: string;
  /** «۰۸:۰۰» */
  clock: string;
  /** «امروز» / «فردا» / «۳ روز دیگر» — how far away it is, for the chip beside it. */
  relative: string;
}

/**
 * Formats the next release of new appointments (Saman `nearest_available_time`, i.e. the product's
 * `next_release_at`) for the full-capacity card and the Dabbeh widget. `null` for a missing or past time.
 */
const DAY = 24 * 3600_000;
/** Iran: +03:30 all year (no daylight saving since 2022). */
const TEHRAN_OFFSET_MINUTES = 210;

/**
 * «زمان اعلام نوبت های جدید» in the profile card's own words: «فردا ساعت ۰۷:۴۵», «امروز ساعت ۱۸:۳۰», or the
 * day further away («شنبه ۲ آبان ساعت ۰۸:۰۰»). `daily`: a time kept from an earlier visit (a queue seat) that
 * has passed moves on day by day to the next one — the doctor releases at the same clock every day.
 */
export const formatReleaseLine = (iso?: string | null, now: Date = new Date(), { daily = false } = {}): string | null => {
  if (!iso) return null;
  let at = new Date(iso).getTime();
  if (!Number.isFinite(at)) return null;
  if (daily && at <= now.getTime()) {
    at += Math.ceil((now.getTime() - at) / DAY) * DAY;
    if (at <= now.getTime()) at += DAY;
  }
  const label = formatReleaseAt(new Date(at).toISOString(), now);
  if (!label) return null;
  const when = label.relative === 'امروز' || label.relative === 'فردا' ? label.relative : label.day;
  return `${when} ساعت ${label.clock}`;
};

export const formatReleaseAt = (iso?: string | null, now: Date = new Date()): ReleaseAtLabel | null => {
  if (!iso) return null;
  // the clinic's clock in Iran (fixed +03:30), whatever the device's time zone — also for «امروز / فردا»
  const at = moment(iso).utcOffset(TEHRAN_OFFSET_MINUTES);
  if (!at.isValid() || at.valueOf() <= now.getTime()) return null;

  const days = at.clone().startOf('day').diff(moment(now).utcOffset(TEHRAN_OFFSET_MINUTES).startOf('day'), 'days');
  return {
    day: digitsEnToFa(at.clone().locale('fa').format('dddd jD jMMMM')),
    weekday: at.clone().locale('fa').format('dddd'),
    clock: digitsEnToFa(at.format('HH:mm')),
    relative: days <= 0 ? 'امروز' : days === 1 ? 'فردا' : `${digitsEnToFa(days)} روز دیگر`,
  };
};
